import { Card, CardContent } from "~/components/ui/card";
import { ArrowRight } from "lucide-react";
import { Link } from "@remix-run/react";
import Container from "~/components/atoms/Container";
import { getRegionUrlPrefixWithCountryCode } from "~/lib/helpers/slugHelper";
import { getFormattedCountryName } from "~/lib/helpers/constants";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import { HOME_PAGE_SECTION_IDS } from "./constants";

export default function OfficeOptions({ region, country }) {

  const categories = [
    {
      title: "Serviced Office",
      description: "Move into a ready-to-use serviced office with all-inclusive amenities and support.",
      image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/space-type-page/managed.avif`,
      link: 'serviced-offices',
      alt: 'Fully furnished serviced office featuring ergonomic desks, shared kitchen, spacious area and modern decor for a move-in-ready workspace'
    },
    {
      title: "Coworking Space",
      description: "Work alongside inspiring teams in flexible, shared coworking spaces built for connection.",
      image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/space-type-page/coworking.avif`,
      link: 'coworking-space',
      alt: 'Collaborative coworking space featuring a shared table where team members engage in a productive group discussion'
    },
    {
      title: "Private Office",
      description: "Secure a fully furnished private office designed for focus, privacy, and productivity.",
      image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/space-type-page/private.avif`,
      link: 'private-offices',
      alt: 'Move-in-ready private office for small to medium-sized businesses, featuring personal space with high privacy'
    },
    {
      title: "Shared Office",
      description: "Enjoy cost-effective shared office spaces where teams collaborate and grow together.",
      image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/space-type-page/shared.avif`,
      link: 'shared-offices',
      alt: 'A collaborative shared office setting with people working at a desk alongside a stylish sofa breakout area'
    },
    {
      title: "Enterprise Office",
      description: "Custom HQ solutions for large, growing teams.",
      image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/space-type-page/enterprise.avif`,
      link: 'enterprise-office-space',
      alt: 'Enterprise office featuring a large-scale floor plan with custom branding options and dedicated collaborative zones for large teams'
    },
    {
      title: "Sublease Office",
      description: "Take over flexible sublet offices and enjoy full control without the long-term lease.",
      image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/space-type-page/sublet.avif`,
      link: 'conventional-sublet-offices',
      alt: 'Plug-and-play sublet office featuring a private layout within an established corporate environment'
    },
  ];

  return (
    <section id={HOME_PAGE_SECTION_IDS.officeOptions} className="bg-gray-50 py-8 md:py-14 scroll-mt-28">
      <Container htmlFor="ultra-wide">
        <div className="mx-auto px-4 text-center">
          {/* Heading */}
          <div className="mb-10">
            <h2 className="text-2xl  md:text-3xl font-bold text-gray-800 ">
              Discover the Right Workspace for Your Business
            </h2>
            <p className="text-md text-gray-600 mt-2 leading-12">
              Explore workspaces across {getFormattedCountryName(region)} that match your goals, from social coworking hubs to private offices and enterprise HQs.
            </p>
          </div>

          {/* Cards */}
          <div className="flex md:grid gap-3 items-stretch md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 mb-10 pb-2 overflow-x-auto scrollbar-hide">
            {categories.map((cat, i) => (
              <Card
                key={i}
                className="relative !min-w-[290px] flex-1 flex flex-col md:flex-row gap-4 md:p-4 shadow-custom border border-gray-200 hover:shadow-md transition group"
              >
                {/* Thumbnail */}
                <img
                  src={cat.image}
                  alt={cat.title}
                  width={176}
                  height={128}
                  className="w-full h-48 md:h-32 md:w-44 rounded-t-lg md:rounded-md object-cover flex-shrink-0"
                  loading="lazy"
                />

                {/* Content */}
                <CardContent className="p-4 pt-0 md:p-0 text-left flex flex-col justify-between flex-1">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      {cat.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">{cat.description}</p>
                  </div>

                  <Link
                    to={`${getRegionUrlPrefixWithCountryCode(region)}/office-space/${cat.link}`}
                    target="_blank"
                    className="text-sm font-medium text-base-red inline-flex items-center gap-1 hover:underline mt-auto after:absolute after:inset-0 after:z-[1]"
                  >
                    Explore Category <ArrowRight className="w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Explore All Button */}
          <Link to={`${getRegionUrlPrefixWithCountryCode(region)}/office-space`} target="_blank">
            <OhButton className="w-full md:w-auto px-6 py-2">
              Explore All Office Type
            </OhButton>
          </Link>
        </div>
      </Container>
    </section>
  );
}
